/**
 * AdminRoute Component
 *
 * Protected route component that only allows admin users to access.
 * Checks if user has admin role in Supabase admin_users table.
 */

import { type ReactNode, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import { supabase } from '../../lib/supabase';

interface AdminRouteProps {
  children: ReactNode;
}

export const AdminRoute = ({ children }: AdminRouteProps) => {
  const { user, isAuthenticated, loading } = useAuth();
  const { error: showError } = useToast();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [checkingAdmin, setCheckingAdmin] = useState(true);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (loading || !isAuthenticated || !user || !user.email) {
        setCheckingAdmin(false);
        return;
      }

      try {
        // Check if user email exists in admin_users table
        const { data, error } = await supabase.rpc('is_admin', {
          user_email: user.email,
        });

        if (error) {
          console.error('[AdminRoute] Error checking admin status:', error);
          setIsAdmin(false);
          setCheckingAdmin(false);
          showError('Failed to verify admin access');
          return;
        }

        const adminStatus = data === true;
        setIsAdmin(adminStatus);
        setCheckingAdmin(false);

        if (!adminStatus) {
          console.warn('[AdminRoute] Access denied for:', user.email);
          showError('Access denied: Admin privileges required');
        } else {
          console.log('[AdminRoute] Admin access granted for:', user.email);
        }
      } catch (err) {
        console.error('[AdminRoute] Exception checking admin status:', err);
        setIsAdmin(false);
        setCheckingAdmin(false);
        showError('Failed to verify admin access');
      }
    };

    checkAdminStatus();
  }, [user, isAuthenticated, loading, showError]);

  // Show loading state while checking auth or admin status
  if (loading || checkingAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-soft-cream dark:bg-gray-900">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Checking permissions...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Redirect to dashboard if not admin (only after check is complete)
  if (isAdmin === false) {
    return <Navigate to="/dashboard" replace />;
  }

  // Only render children if explicitly admin (isAdmin === true)
  if (isAdmin === true) {
    return <>{children}</>;
  }

  // Should never reach here, but just in case
  return null;
};
